import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
} from 'styled-system';
import { borderColor, borderRadius, borderStyle, borderWidth } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { Text } from 'common/components/Text';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { RenderingType } from '../enums';
import ConfigurationCardHeader from './ConfigurationCardHeader';
import ConfigurationGroup from './ConfigurationGroup';
import type { ArticleVariationGroup } from './types';

interface ConfigurationCardProps {
  groupTitle?: string;
  groups: ArticleVariationGroup[];
  index: number;
  optionTitle: string;
  renderingType: RenderingType;
  selectedURL: Nullable<string>;
  title: string;
  onSelect: (sku: Nullable<string>) => void;
  listLength: number;
}

const UnstyledContainer = styled(Flex)<
  BorderColorProps & BorderRadiusProps & BorderWidthProps & BorderStyleProps
>`
  ${borderColor};
  ${borderRadius};
  ${borderStyle};
  ${borderWidth};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer
    as="section"
    borderColor="sys.neutral.background.weak"
    borderRadius="medium"
    borderStyle="solid"
    borderWidth="1px"
    flexDirection="column"
    {...props}
  />
);

const UnstyledDescription = styled(Text)``;

const Description = (props: ComponentProps<typeof UnstyledDescription>) => (
  <UnstyledDescription
    color="sys.neutral.text.default"
    fontSize="fs_14"
    lineHeight="lh_20"
    mb="sp_16"
    {...props}
  />
);

const ConfigurationCard = ({
  groupTitle,
  groups,
  index,
  optionTitle,
  title,
  ...groupProps
}: ConfigurationCardProps) => {
  const { r } = useLocalization();
  const titlePrefix = r.t('h24_configuration_trap_alternative', {
    index: index + 1,
  });

  return (
    <Container>
      <ConfigurationCardHeader title={`${titlePrefix} ${title}`} />
      <Flex p="sp_16" flexDirection="column">
        {groupTitle && (
          <Description>
            {r.t('h24_configuration_trap_choose_alternative_option', {
              selection: groupTitle,
              title: optionTitle,
            })}
          </Description>
        )}

        <Flex flexDirection="column">
          {groups.map((groupItem, groupIndex) => (
            <ConfigurationGroup
              {...groupProps}
              {...groupItem}
              titlePrefix={titlePrefix}
              key={`group-item-${groupIndex}`}
            />
          ))}
        </Flex>
      </Flex>
    </Container>
  );
};

export default ConfigurationCard;
